import React, { useState, useEffect } from 'react';
import { 
  validateTonnage, 
  formatTonnage, 
  roundTonnage,
  getInputConfig,
  allowsFractionalTons
} from '../../utils/tonnage';

interface TonnageInputProps {
  value: number;
  onChange: (value: number) => void;
  systemType: string;
  category?: 'weapons' | 'optionalSystems' | 'powerSystems' | 'fuelSystems';
  max?: number;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showValidation?: boolean;
  onValidationChange?: (isValid: boolean, error?: string) => void;
}

/**
 * Reusable tonnage input component with validation based on Traveller rules
 */
export const TonnageInput: React.FC<TonnageInputProps> = ({
  value,
  onChange,
  systemType,
  category = 'optionalSystems',
  max,
  label,
  placeholder = "Enter tonnage",
  className = "",
  disabled = false,
  showValidation = true,
  onValidationChange
}) => {
  const [inputValue, setInputValue] = useState(value.toString());
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isValid, setIsValid] = useState(true);

  // Get input configuration for this system type
  const config = getInputConfig(systemType as any, category);
  const allowsFractions = allowsFractionalTons(systemType as any, category);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  // Validate input and update state
  useEffect(() => {
    const numValue = parseFloat(inputValue) || 0;
    const validation = validateTonnage(numValue, systemType as any, category);
    
    // Check max constraint if provided
    if (max && numValue > max) {
      validation.isValid = false;
      validation.error = `Maximum ${formatTonnage(max)} allowed`;
      validation.correctedValue = max;
    }

    setError(validation.error || null);
    setIsValid(validation.isValid);

    // Notify parent of validation state
    if (onValidationChange) {
      onValidationChange(validation.isValid, validation.error);
    }

    // Auto-correct if validation provides a corrected value
    if (!validation.isValid && validation.correctedValue !== undefined) {
      const correctedValue = validation.correctedValue;
      if (Math.abs(numValue - correctedValue) > 0.001) {
        setInputValue(correctedValue.toString());
        onChange(correctedValue);
      }
    }
  }, [inputValue, systemType, category, max, onChange, onValidationChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Parse and validate the new value
    const numValue = parseFloat(newValue);
    if (!isNaN(numValue)) {
      // Round to nearest valid increment
      const roundedValue = roundTonnage(numValue, config.step);
      onChange(roundedValue);
    } else if (newValue === '' || newValue === '-') {
      // Allow empty or minus sign for typing
      setInputValue(newValue);
    }
  };

  const handleBlur = () => {
    // On blur, ensure we have a valid value
    const numValue = parseFloat(inputValue) || 0;
    const roundedValue = roundTonnage(numValue, config.step);
    
    if (roundedValue < config.min) {
      setInputValue(config.min.toString());
      onChange(config.min);
    } else if (max && roundedValue > max) {
      setInputValue(max.toString());
      onChange(max);
    } else {
      setInputValue(roundedValue.toString());
      onChange(roundedValue);
    }
  };

  const getStepAttribute = () => {
    if (config.step === 0) return undefined;
    return config.step.toString();
  };

  const getMinAttribute = () => {
    if (config.min === 0) return "0";
    return config.min.toString();
  };

  const getMaxAttribute = () => {
    if (max !== undefined) return max.toString();
    if (config.max !== undefined) return config.max.toString();
    return undefined;
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {allowsFractions && (
            <span className="text-xs text-gray-500 ml-1">
              (allows fractional tons)
            </span>
          )}
        </label>
      )}
      
      <input
        type="number"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        step={getStepAttribute()}
        min={getMinAttribute()}
        max={getMaxAttribute()}
        placeholder={placeholder}
        disabled={disabled}
        className={`
          w-full rounded-md border px-3 py-2 focus:outline-none focus:ring-1
          ${error 
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
            : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
          }
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
        `}
      />
      
      {showValidation && error && (
        <p className="text-sm text-red-600">
          {error}
        </p>
      )}
      
      {showValidation && allowsFractions && (
        <p className="text-xs text-gray-500">
          Step: {config.step} ton{config.step !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
};

export default TonnageInput;
